// src/stores/map/mapSelectors.ts
//
// Named selectors for the Zustand map store. Every selector that components
// consume must be a named export — ad hoc inline selectors inside components
// are prohibited.
//
// Compound selectors (selectMapboxWrapperDeps, selectNetworkVisualizationDeps)
// return plain objects. Consumers MUST use `useShallow` to avoid unnecessary
// re-renders:
//   const deps = useMapStore(useShallow(selectMapboxWrapperDeps));
import type { Geometry, Position } from "@turf/helpers";
import type { MapLayer } from "types/maps/entities";
import type { RigFracFocusAttribute } from "utils/color/focusColorSync/triggers";

import type { IMapResult } from "api/map";

import type {
  ConfirmationChoice,
  Envelope,
  FlyToExtent,
  FlyToExtentCommand,
  FlyToExtentOptions,
  FlyToSpecificPoint,
  INetworkDepthLegend,
  ISelectedWell,
  LayerStyleSetting,
  MapStoreDisplaySettings,
  RigFracColorSource,
  RigFracLayerFilter,
  SelectedRigFracLayer,
  WellNetworkRequest
} from "./mapTypes";

// ---------------------------------------------------------------------------
// Store shape — local type alias so selectors can be typed without importing
// the full store (avoids circular dependency with mapStore.ts).
// ---------------------------------------------------------------------------

/** Read-only projection of the store state used by selectors. */
interface MapStoreState {
  readonly txnId: IMapResult;
  readonly selectedWells: Readonly<Record<string, ISelectedWell>>;
  readonly selectedFacilities: Readonly<Record<string, ISelectedWell>>;
  readonly attentionWells: readonly string[];
  readonly jumpHighlightWells: readonly string[];
  readonly typeLogWells: readonly ISelectedWell[];
  readonly selectedRigFracLayer: Maybe<SelectedRigFracLayer>;
  readonly hoveredRigFracId: Maybe<string>;
  readonly rigFracFocusColorBy: Maybe<RigFracFocusAttribute>;
  readonly rigFracColorByDetached: Readonly<Record<string, true>>;
  readonly rigFracFilterOptions: Readonly<
    Record<string, Readonly<Record<string, readonly string[]>>>
  >;
  readonly displaySettings: MapStoreDisplaySettings;
  readonly layers: readonly MapLayer[];
  readonly baseLayerResourcePlays: Maybe<readonly string[]>;
  readonly baseLayerFilterId: Maybe<string>;
  readonly ipdbLayersLoaded: {
    readonly geoModel: boolean;
    readonly controlWells: boolean;
  };
  readonly geoModelLoading: boolean;
  readonly isDebuggingMap: boolean;
  readonly mapState: {
    readonly lat: number;
    readonly lng: number;
    readonly zoom: number;
  };
  readonly viewLock: boolean;
  readonly mapExtent: GeoJSON.Polygon;
  readonly isMapMoving: boolean;
  readonly mapMoverCount: number;
  readonly flyToGroup: Maybe<string>;
  readonly flyToSpecificPoint: Maybe<FlyToSpecificPoint>;
  readonly flyToExtent: Maybe<FlyToExtentCommand>;
  readonly attentionPolygon: Maybe<GeoJSON.Polygon | GeoJSON.MultiPolygon>;
  readonly exploitableArea: readonly Geometry[];
  readonly xdaInterceptData: {
    readonly line: readonly Position[];
    readonly points: readonly Position[];
  };
  readonly xdaLineRequest: {
    readonly line: readonly Position[];
    readonly wells: readonly string[];
  };
  readonly networkDepthLegend: INetworkDepthLegend;
  readonly showNetworkOptions: boolean;
  readonly highlightContributingWellsForFacility: Maybe<string>;
  readonly wellNetworkMapRequest: WellNetworkRequest;
  readonly isWellNetworkFilterActivated: boolean;
  readonly networkEnvelope: Maybe<Envelope>;
  readonly salesEnvelope: Maybe<Envelope>;
  readonly hasToConfirmSize: boolean;
  readonly facilityHasToConfirmSize: boolean;
  readonly facilityOverrideSizeLimit: boolean;
  readonly wellsConfirmationChoice: Maybe<ConfirmationChoice>;
  readonly facilityConfirmationChoice: Maybe<ConfirmationChoice>;

  // Actions (stable references — Zustand actions never change identity)
  readonly setTxnId: (result: IMapResult) => void;
  readonly setSelectedWells: (wells: Record<string, ISelectedWell>) => void;
  readonly setSelectedFacilities: (facilities: Record<string, ISelectedWell>) => void;
  readonly setSelectedEntities: (
    entityKind: string,
    entities: Record<string, ISelectedWell>
  ) => void;
  readonly setAttentionWells: (uwis: string[]) => void;
  readonly setJumpHighlightWells: (uwis: string[]) => void;
  readonly setTypeLogWells: (wells: ISelectedWell[]) => void;
  readonly setSelectedRigFracLayer: (layer: Maybe<SelectedRigFracLayer>) => void;
  readonly setHiddenRigFracValues: (layerId: string, values: string[]) => void;
  readonly clearHiddenRigFracValues: () => void;
  readonly setRigFracColorSource: (layerId: string, source: RigFracColorSource) => void;
  readonly clearRigFracColorSource: () => void;
  readonly setHoveredRigFracId: (id: Maybe<string>) => void;
  readonly setRigFracFocusColorBy: (field: RigFracFocusAttribute) => void;
  readonly detachRigFracColorBy: (layerId: string) => void;
  readonly attachRigFracColorBy: (layerId: string) => void;
  readonly clearRigFracFocusColorBy: () => void;
  readonly setDisplaySettings: (settings: MapStoreDisplaySettings) => void;
  readonly setLabelDensity: (layer: string, value: number) => void;
  readonly setLayerFilter: (
    layerId: string,
    field: keyof RigFracLayerFilter,
    values: string[]
  ) => void;
  readonly setRigFracFilterOptions: (
    layerId: string,
    options: Readonly<Record<string, readonly string[]>>
  ) => void;
  readonly setLayers: (layers: MapLayer[]) => void;
  readonly setBaseLayerResourcePlays: (plays: Maybe<string[]>) => void;
  readonly setBaseLayerFilterId: (filterId: Maybe<string>) => void;
  readonly setIpdbGeoModelLoaded: (loaded: boolean) => void;
  readonly setIpdbControlWellsLoaded: (loaded: boolean) => void;
  readonly setGeoModelLoading: (loading: boolean) => void;
  readonly setIsDebuggingMap: (debugging: boolean) => void;
  readonly setMapState: (state: { lat: number; lng: number; zoom: number }) => void;
  readonly setViewLock: (locked: boolean) => void;
  readonly setMapExtent: (extent: GeoJSON.Polygon) => void;
  readonly beginMapMove: () => void;
  readonly endMapMove: () => void;
  readonly setFlyToGroup: (group: Maybe<string>) => void;
  readonly setFlyToSpecificPoint: (point: Maybe<FlyToSpecificPoint>) => void;
  readonly setFlyToExtent: (
    extent: Maybe<FlyToExtent>,
    options?: FlyToExtentOptions
  ) => void;
  readonly setAttentionPolygon: (polygon: Maybe<GeoJSON.Polygon>) => void;
  readonly setExploitableArea: (area: Geometry[]) => void;
  readonly setXdaInterceptData: (data: { line: Position[]; points: Position[] }) => void;
  readonly setXdaLineRequest: (request: { line: Position[]; wells: string[] }) => void;
  readonly setNetworkDepthLegend: (legend: INetworkDepthLegend) => void;
  readonly clearNetworkDepthLegend: () => void;
  readonly setShowNetworkOptions: (show: boolean) => void;
  readonly setHighlightContributingWellsForFacility: (facilityId: string) => void;
  readonly clearHighlightContributingWellsForFacility: () => void;
  readonly setWellNetworkMapRequest: (request: WellNetworkRequest) => void;
  readonly clearWellNetworkMapRequest: () => void;
  readonly setWellNetworkFilterActivated: (activated: boolean) => void;
  readonly setNetworkEnvelope: (envelope: Maybe<Envelope>) => void;
  readonly setSalesEnvelope: (envelope: Maybe<Envelope>) => void;
  readonly setHasToConfirmSize: (confirm: boolean) => void;
  readonly setFacilityHasToConfirmSize: (confirm: boolean) => void;
  readonly setFacilityOverrideSizeLimit: (override: boolean) => void;
  readonly setWellsConfirmationChoice: (choice: Maybe<ConfirmationChoice>) => void;
  readonly setFacilityConfirmationChoice: (choice: Maybe<ConfirmationChoice>) => void;
  readonly reset: () => void;
}

// Re-export the local alias so that test files and other modules can
// reference it without importing the full store module.
export type { MapStoreState };

// ---------------------------------------------------------------------------
// Actions interface — used by selectMapActions and the bridge shim.
// ---------------------------------------------------------------------------

export interface MapActions {
  readonly setTxnId: (result: IMapResult) => void;
  readonly setSelectedWells: (wells: Record<string, ISelectedWell>) => void;
  readonly setSelectedFacilities: (facilities: Record<string, ISelectedWell>) => void;
  readonly setSelectedEntities: (
    entityKind: string,
    entities: Record<string, ISelectedWell>
  ) => void;
  readonly setAttentionWells: (uwis: string[]) => void;
  readonly setJumpHighlightWells: (uwis: string[]) => void;
  readonly setTypeLogWells: (wells: ISelectedWell[]) => void;
  readonly setSelectedRigFracLayer: (layer: Maybe<SelectedRigFracLayer>) => void;
  readonly setHiddenRigFracValues: (layerId: string, values: string[]) => void;
  readonly clearHiddenRigFracValues: () => void;
  readonly setRigFracColorSource: (layerId: string, source: RigFracColorSource) => void;
  readonly clearRigFracColorSource: () => void;
  readonly setHoveredRigFracId: (id: Maybe<string>) => void;
  readonly setRigFracFocusColorBy: (field: RigFracFocusAttribute) => void;
  readonly detachRigFracColorBy: (layerId: string) => void;
  readonly attachRigFracColorBy: (layerId: string) => void;
  readonly clearRigFracFocusColorBy: () => void;
  readonly setDisplaySettings: (settings: MapStoreDisplaySettings) => void;
  readonly setLabelDensity: (layer: string, value: number) => void;
  readonly setLayerFilter: (
    layerId: string,
    field: keyof RigFracLayerFilter,
    values: string[]
  ) => void;
  readonly setRigFracFilterOptions: (
    layerId: string,
    options: Readonly<Record<string, readonly string[]>>
  ) => void;
  readonly setLayers: (layers: MapLayer[]) => void;
  readonly setBaseLayerResourcePlays: (plays: Maybe<string[]>) => void;
  readonly setBaseLayerFilterId: (filterId: Maybe<string>) => void;
  readonly setIpdbGeoModelLoaded: (loaded: boolean) => void;
  readonly setIpdbControlWellsLoaded: (loaded: boolean) => void;
  readonly setGeoModelLoading: (loading: boolean) => void;
  readonly setIsDebuggingMap: (debugging: boolean) => void;
  readonly setMapState: (state: { lat: number; lng: number; zoom: number }) => void;
  readonly setViewLock: (locked: boolean) => void;
  readonly setMapExtent: (extent: GeoJSON.Polygon) => void;
  readonly beginMapMove: () => void;
  readonly endMapMove: () => void;
  readonly setFlyToGroup: (group: Maybe<string>) => void;
  readonly setFlyToSpecificPoint: (point: Maybe<FlyToSpecificPoint>) => void;
  readonly setFlyToExtent: (
    extent: Maybe<FlyToExtent>,
    options?: FlyToExtentOptions
  ) => void;
  readonly setAttentionPolygon: (polygon: Maybe<GeoJSON.Polygon>) => void;
  readonly setExploitableArea: (area: Geometry[]) => void;
  readonly setXdaInterceptData: (data: { line: Position[]; points: Position[] }) => void;
  readonly setXdaLineRequest: (request: { line: Position[]; wells: string[] }) => void;
  readonly setNetworkDepthLegend: (legend: INetworkDepthLegend) => void;
  readonly clearNetworkDepthLegend: () => void;
  readonly setShowNetworkOptions: (show: boolean) => void;
  readonly setHighlightContributingWellsForFacility: (facilityId: string) => void;
  readonly clearHighlightContributingWellsForFacility: () => void;
  readonly setWellNetworkMapRequest: (request: WellNetworkRequest) => void;
  readonly clearWellNetworkMapRequest: () => void;
  readonly setWellNetworkFilterActivated: (activated: boolean) => void;
  readonly setNetworkEnvelope: (envelope: Maybe<Envelope>) => void;
  readonly setSalesEnvelope: (envelope: Maybe<Envelope>) => void;
  readonly setHasToConfirmSize: (confirm: boolean) => void;
  readonly setFacilityHasToConfirmSize: (confirm: boolean) => void;
  readonly setFacilityOverrideSizeLimit: (override: boolean) => void;
  readonly setWellsConfirmationChoice: (choice: Maybe<ConfirmationChoice>) => void;
  readonly setFacilityConfirmationChoice: (choice: Maybe<ConfirmationChoice>) => void;
  readonly reset: () => void;
}

// ═══════════════════════════════════════════════════════════════════════════
// Transaction / Map Results
// ═══════════════════════════════════════════════════════════════════════════

/** Full txnId object (well + facility map result). */
export const selectTxnId = (s: MapStoreState): IMapResult => s.txnId;

/** Well transaction ID string. */
export const selectTxnIdId = (s: MapStoreState): Maybe<string> => s.txnId?.id;

/** Facility transaction ID string. */
export const selectTxnIdFacilityId = (s: MapStoreState): Maybe<string> =>
  s.txnId?.facilityId;

/** Active filter ID from the last map request. */
export const selectTxnIdFilterId = (s: MapStoreState): Maybe<string> => s.txnId?.filterId;

/** Bounding box of the current map result. */
export const selectTxnIdBBox = (
  s: MapStoreState
): Maybe<{ sw: number[]; ne: number[] }> => s.txnId?.bBox;

/** Well legend (legendItems + title). */
export const selectWellLegend = (s: MapStoreState): Maybe<IMapResult["legend"]> =>
  s.txnId?.legend;

/** Well legend items array. */
export const selectWellLegendItems = (
  s: MapStoreState
): Maybe<IMapResult["legend"]["legendItems"]> => s.txnId?.legend?.legendItems;

/** Facility legend (legendItems + title). */
export const selectFacilityLegend = (
  s: MapStoreState
): Maybe<IMapResult["facilityLegend"]> => s.txnId?.facilityLegend;

/** Facility legend items array. */
export const selectFacilityLegendItems = (
  s: MapStoreState
): Maybe<IMapResult["facilityLegend"]["legendItems"]> =>
  s.txnId?.facilityLegend?.legendItems;

// ═══════════════════════════════════════════════════════════════════════════
// Entity Selection
// ═══════════════════════════════════════════════════════════════════════════

/** Record of currently selected wells keyed by UWI. */
export const selectSelectedWells = (
  s: MapStoreState
): Readonly<Record<string, ISelectedWell>> => s.selectedWells;

/** Record of currently selected facilities keyed by facility ID. */
export const selectSelectedFacilities = (
  s: MapStoreState
): Readonly<Record<string, ISelectedWell>> => s.selectedFacilities;

/** Array of UWIs that should receive the attention highlight. */
export const selectAttentionWells = (s: MapStoreState): readonly string[] =>
  s.attentionWells;

/** UWIs highlighted by a Jump to Location well-list jump — durable,
 *  survives camera movement and chart hover. See mapTypes.ts's field JSDoc. */
export const selectJumpHighlightWells = (s: MapStoreState): readonly string[] =>
  s.jumpHighlightWells;

/** Wells designated for type-log display. */
export const selectTypeLogWells = (s: MapStoreState): readonly ISelectedWell[] =>
  s.typeLogWells;

/** Rig/frac layer drilled into for the Color By details panel (null = layer list). */
export const selectSelectedRigFracLayer = (
  s: MapStoreState
): Maybe<SelectedRigFracLayer> => s.selectedRigFracLayer;

// Frozen empty fallbacks (NOT inline literals — a fresh object per call would
// defeat referential-equality and re-render/notify every consumer on any store
// change). Needed because hiddenRigFracValues / rigFracColorSource were
// relocated INTO displaySettings (EVA-63), and setDisplaySettings replaces
// displaySettings wholesale with no defaults merge — so a whole object that
// predates them leaves these keys undefined, and consumers index straight into the result
// (before their own `?? []` runs) and would throw. Same guard style as
// selectLayerFilterSettings below.
const EMPTY_HIDDEN_RIG_FRAC_VALUES: Readonly<Record<string, readonly string[]>> =
  Object.freeze({});
const EMPTY_RIG_FRAC_COLOR_SOURCE: Readonly<Record<string, RigFracColorSource>> =
  Object.freeze({});

/** Per-layer sets of hidden rig/frac attribute values (EVA-63: lives in displaySettings so it rides View capture/restore). */
export const selectHiddenRigFracValues = (
  s: MapStoreState
): Readonly<Record<string, readonly string[]>> =>
  s.displaySettings.hiddenRigFracValues ?? EMPTY_HIDDEN_RIG_FRAC_VALUES;

/** Per-layer remembered colouring source (EVA-63: lives in displaySettings so it rides View capture/restore). */
export const selectRigFracColorSource = (
  s: MapStoreState
): Readonly<Record<string, RigFracColorSource>> =>
  s.displaySettings.rigFracColorSource ?? EMPTY_RIG_FRAC_COLOR_SOURCE;

/** `id` of the rig/frac feature currently hovered on the map (null = none). */
export const selectHoveredRigFracId = (s: MapStoreState): Maybe<string> =>
  s.hoveredRigFracId;

/** Per-layer derived available filter options (EVA-63 revision). Keyed by layerId. */
export const selectRigFracFilterOptions = (
  s: MapStoreState
): Readonly<Record<string, Readonly<Record<string, readonly string[]>>>> =>
  s.rigFracFilterOptions;

// ═══════════════════════════════════════════════════════════════════════════
// Display Settings
// ═══════════════════════════════════════════════════════════════════════════

/** Full display settings object (line thickness, label sizes, etc.). */
export const selectDisplaySettings = (s: MapStoreState): MapStoreDisplaySettings =>
  s.displaySettings;

/** Per-layer label density map (values clamped to [0, 1]). */
export const selectLabelDensity = (s: MapStoreState): Readonly<Record<string, number>> =>
  s.displaySettings.labelDensity;

/** Per-layer zoom range settings. */
export const selectLayerZoomSettings = (
  s: MapStoreState
): Readonly<Record<string, { minzoom?: number; maxzoom?: number }>> =>
  s.displaySettings.layerZoomSettings;

/**
 * Module-level frozen constant — NOT an inline `{}` literal. An inline literal
 * would be a fresh object on every call, defeating Zustand's referential-equality
 * check and causing consumers to re-render on every store change.
 */
const EMPTY_LAYER_FILTER_SETTINGS: Readonly<Record<string, RigFracLayerFilter>> =
  Object.freeze({});

/**
 * Per-layer Rig/Frac attribute filter selections (EVA-63), keyed by layer ID.
 * Falls back to a frozen empty object when `layerFilterSettings` is undefined
 * (a wholesale setDisplaySettings replace can leave the field unset).
 */
export const selectLayerFilterSettings = (
  s: MapStoreState
): Readonly<Record<string, RigFracLayerFilter>> =>
  s.displaySettings.layerFilterSettings ?? EMPTY_LAYER_FILTER_SETTINGS;

const EMPTY_LAYER_STYLE_SETTINGS: Readonly<Record<string, LayerStyleSetting>> =
  Object.freeze({});

/** Per-layer user style (EVA-351), keyed by layer name. Frozen-empty when the key is absent. */
export const selectLayerStyleSettings = (
  s: MapStoreState
): Readonly<Record<string, LayerStyleSetting>> =>
  s.displaySettings.layerStyleSettings ?? EMPTY_LAYER_STYLE_SETTINGS;

/**
 * The rig/frac-owned subset of display settings that a project View persists:
 * attribute filters (EVA-63), hidden values + colour source (EVA-64), and icon
 * sizes. Consumed by the View dirty-tracker so an edit in the rig/frac area
 * marks the selected View "(unsaved)".
 *
 * COMPOUND selector — returns a fresh object literal, so it must be consumed
 * with a shallow equality function (e.g. `useMapStore.subscribe(..., { equalityFn: shallow })`
 * or `useShallow`), never a plain `===` comparison.
 */
export const selectRigFracDisplaySettings = (s: MapStoreState) => ({
  layerFilterSettings: s.displaySettings.layerFilterSettings,
  hiddenRigFracValues: s.displaySettings.hiddenRigFracValues,
  rigFracColorSource: s.displaySettings.rigFracColorSource,
  rigIconSize: s.displaySettings.rigIconSize,
  fracIconSize: s.displaySettings.fracIconSize
});

/** Ordered array of map layers (visibility, ordering). */
export const selectLayers = (s: MapStoreState): readonly MapLayer[] => s.layers;

/** Resource play filter for the base well layer. */
export const selectBaseLayerResourcePlays = (
  s: MapStoreState
): Maybe<readonly string[]> => s.baseLayerResourcePlays;

/** Setter for the base well layer's resource-play selection — a stable action reference. */
export const selectSetBaseLayerResourcePlays = (
  s: MapStoreState
): MapStoreState["setBaseLayerResourcePlays"] => s.setBaseLayerResourcePlays;

/** The base well layer's own filterId (EVA-35) — see useBaseLayerFilterSync. */
export const selectBaseLayerFilterId = (s: MapStoreState): Maybe<string> =>
  s.baseLayerFilterId;

/** Setter for the base well layer's own filterId (EVA-35) — a stable action reference. */
export const selectSetBaseLayerFilterId = (
  s: MapStoreState
): MapStoreState["setBaseLayerFilterId"] => s.setBaseLayerFilterId;

/** Whether the IPDB geo-model layer has finished loading. */
export const selectIpdbGeoModelLoaded = (s: MapStoreState): boolean =>
  s.ipdbLayersLoaded.geoModel;

/** Whether the IPDB control-wells layer has finished loading. */
export const selectIpdbControlWellsLoaded = (s: MapStoreState): boolean =>
  s.ipdbLayersLoaded.controlWells;

/** True while a geo-model load is in-flight; false once settled (success or error). */
export const selectGeoModelLoading = (s: MapStoreState): boolean => s.geoModelLoading;

/** Whether the map debug overlay is enabled. */
export const selectIsDebuggingMap = (s: MapStoreState): boolean => s.isDebuggingMap;

/**
 * Selector factory: returns true if the layer with the given ID is visible.
 *
 * Usage:
 * ```
 * const isVisible = useMapStore(selectLayerIsVisibleById("wells"));
 * ```
 */
export const selectLayerIsVisibleById =
  (layerId: string) =>
  (s: MapStoreState): boolean => {
    const layer = s.layers?.find(
      (l: { id: string; isVisible: boolean }) => l.id === layerId
    );
    return layer?.isVisible ?? false;
  };

// ═══════════════════════════════════════════════════════════════════════════
// Viewport / Navigation
// ═══════════════════════════════════════════════════════════════════════════

/** Current map center + zoom. */
export const selectMapState = (
  s: MapStoreState
): { readonly lat: number; readonly lng: number; readonly zoom: number } => s.mapState;

/** Current map zoom level. */
export const selectMapZoom = (s: MapStoreState): number => s.mapState.zoom;

/** Whether the map viewport is locked (prevents pan/zoom). */
export const selectViewLock = (s: MapStoreState): boolean => s.viewLock;

/** Current map extent as a GeoJSON Polygon. */
export const selectMapExtent = (s: MapStoreState): GeoJSON.Polygon => s.mapExtent;

/** Whether the map camera is moving right now (a user gesture or a fly-to). */
export const selectIsMapMoving = (s: MapStoreState): boolean => s.isMapMoving;

/** Group ID to fly the map camera to. */
export const selectFlyToGroup = (s: MapStoreState): Maybe<string> => s.flyToGroup;

/** Specific lat/lng/zoom to fly the map camera to. */
export const selectFlyToSpecificPoint = (s: MapStoreState): Maybe<FlyToSpecificPoint> =>
  s.flyToSpecificPoint;

/**
 * Queued extent to fit the map camera to. Deliberately left in the store after
 * a fit: each map instance tracks the newest command it has consumed
 * (useMapNavigation), so several maps on one page all fit the same pick.
 */
export const selectFlyToExtent = (s: MapStoreState): Maybe<FlyToExtentCommand> =>
  s.flyToExtent;

/** Polygon to draw attention to on the map. */
export const selectAttentionPolygon = (s: MapStoreState): Maybe<GeoJSON.Polygon> =>
  s.attentionPolygon;

/** Exploitable area geometries to render on the map. */
export const selectExploitableArea = (s: MapStoreState): readonly Geometry[] =>
  s.exploitableArea;

// ═══════════════════════════════════════════════════════════════════════════
// XDA Cross-Section
// ═══════════════════════════════════════════════════════════════════════════

/** XDA intercept line + intersection points. */
export const selectXdaInterceptData = (
  s: MapStoreState
): {
  readonly line: readonly Position[];
  readonly points: readonly Position[];
} => s.xdaInterceptData;

/** XDA line request (line geometry + associated wells). */
export const selectXdaLineRequest = (
  s: MapStoreState
): {
  readonly line: readonly Position[];
  readonly wells: readonly string[];
} => s.xdaLineRequest;

/**
 * Derived selector: is there a cross-section line to draw at all? Says nothing
 * about which flow wrote it — an auto-derived line and a user-drawn one are
 * the same entry in this store.
 */
export const selectHasXdaLine = (s: MapStoreState): boolean =>
  (s.xdaLineRequest?.line?.length ?? 0) > 0;

/**
 * Derived selector: returns true when the XDA line's first point (A) is
 * east of the second point (A'), indicating the line direction is reversed.
 * Returns false when the line has fewer than 2 points.
 */
export const selectIsXdaLineReversed = (s: MapStoreState): boolean => {
  const line = s.xdaLineRequest?.line;
  if (!line || line.length < 2) return false;
  return line[0][0] > line[1][0];
};

// ═══════════════════════════════════════════════════════════════════════════
// Midstream Network Visualization
// ═══════════════════════════════════════════════════════════════════════════

/** Network depth legend (items, visibility, title). */
export const selectNetworkDepthLegend = (s: MapStoreState): INetworkDepthLegend =>
  s.networkDepthLegend;

/** Whether the network options panel is visible. */
export const selectShowNetworkOptions = (s: MapStoreState): boolean =>
  s.showNetworkOptions;

/** Facility ID whose contributing wells should be highlighted. */
export const selectHighlightContributingWellsForFacility = (
  s: MapStoreState
): Maybe<string> => s.highlightContributingWellsForFacility;

/** Well network map request parameters. */
export const selectWellNetworkMapRequest = (s: MapStoreState): WellNetworkRequest =>
  s.wellNetworkMapRequest;

/** Whether the well-network filter is activated (triggers pan-to-bounds). */
export const selectIsWellNetworkFilterActivated = (s: MapStoreState): boolean =>
  s.isWellNetworkFilterActivated;

/** Network visualization bounding envelope. */
export const selectNetworkEnvelope = (s: MapStoreState): Maybe<Envelope> =>
  s.networkEnvelope;

/** Sales facility bounding envelope. */
export const selectSalesEnvelope = (s: MapStoreState): Maybe<Envelope> => s.salesEnvelope;

// ═══════════════════════════════════════════════════════════════════════════
// Series Size Confirmation
// ═══════════════════════════════════════════════════════════════════════════

/** Whether the well series size confirmation dialog should be shown. */
export const selectHasToConfirmSize = (s: MapStoreState): boolean => s.hasToConfirmSize;

/** Whether the facility series size confirmation dialog should be shown. */
export const selectFacilityHasToConfirmSize = (s: MapStoreState): boolean =>
  s.facilityHasToConfirmSize;

/** Whether the facility size limit has been overridden. */
export const selectFacilityOverrideSizeLimit = (s: MapStoreState): boolean =>
  s.facilityOverrideSizeLimit;

/** User's choice for the wells series size confirmation dialog. */
export const selectWellsConfirmationChoice = (
  s: MapStoreState
): Maybe<ConfirmationChoice> => s.wellsConfirmationChoice;

/** User's choice for the facility series size confirmation dialog. */
export const selectFacilityConfirmationChoice = (
  s: MapStoreState
): Maybe<ConfirmationChoice> => s.facilityConfirmationChoice;

// ═══════════════════════════════════════════════════════════════════════════
// Compound Selectors
//
// These return plain objects. Consumers MUST use `useShallow` to get
// shallow-equality comparison and avoid unnecessary re-renders:
//   const deps = useMapStore(useShallow(selectMapboxWrapperDeps));
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Compound selector for MapboxWrapper — the 10 fields it reads from map state.
 * Consumers MUST use `useShallow`.
 */
export const selectMapboxWrapperDeps = (s: MapStoreState) => ({
  attentionPolygon: s.attentionPolygon,
  attentionWells: s.attentionWells,
  displaySettings: s.displaySettings,
  flyToGroup: s.flyToGroup,
  viewLock: s.viewLock,
  mapState: s.mapState,
  flyToSpecificPoint: s.flyToSpecificPoint,
  selectedWells: s.selectedWells,
  txnId: s.txnId,
  baseLayerResourcePlays: s.baseLayerResourcePlays
});

/**
 * Compound selector for NetworkVisualizationOverlay.
 * Consumers MUST use `useShallow`.
 */
export const selectNetworkVisualizationDeps = (s: MapStoreState) => ({
  wellNetworkMapRequest: s.wellNetworkMapRequest,
  showNetworkOptions: s.showNetworkOptions,
  networkDepthLegend: s.networkDepthLegend,
  displaySettings: s.displaySettings,
  selectedWells: s.selectedWells,
  isWellNetworkFilterActivated: s.isWellNetworkFilterActivated
});

// ═══════════════════════════════════════════════════════════════════════════
// Action Selector
//
// Returns all actions from the store. Action references are stable (Zustand
// actions never change identity), but this selector returns a new wrapper
// object. Consumers MUST use `useShallow`:
//   const actions = useMapStore(useShallow(selectMapActions));
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Returns all store actions as a single object.
 * Consumers MUST use `useShallow` to avoid re-renders from the wrapper object.
 */
export const selectMapActions = (s: MapStoreState): MapActions => ({
  setTxnId: s.setTxnId,
  setSelectedWells: s.setSelectedWells,
  setSelectedFacilities: s.setSelectedFacilities,
  setSelectedEntities: s.setSelectedEntities,
  setAttentionWells: s.setAttentionWells,
  setJumpHighlightWells: s.setJumpHighlightWells,
  setTypeLogWells: s.setTypeLogWells,
  setSelectedRigFracLayer: s.setSelectedRigFracLayer,
  setHiddenRigFracValues: s.setHiddenRigFracValues,
  clearHiddenRigFracValues: s.clearHiddenRigFracValues,
  setRigFracColorSource: s.setRigFracColorSource,
  clearRigFracColorSource: s.clearRigFracColorSource,
  setHoveredRigFracId: s.setHoveredRigFracId,
  setRigFracFocusColorBy: s.setRigFracFocusColorBy,
  detachRigFracColorBy: s.detachRigFracColorBy,
  attachRigFracColorBy: s.attachRigFracColorBy,
  clearRigFracFocusColorBy: s.clearRigFracFocusColorBy,
  setDisplaySettings: s.setDisplaySettings,
  setLabelDensity: s.setLabelDensity,
  setLayerFilter: s.setLayerFilter,
  setRigFracFilterOptions: s.setRigFracFilterOptions,
  setLayers: s.setLayers,
  setBaseLayerResourcePlays: s.setBaseLayerResourcePlays,
  setBaseLayerFilterId: s.setBaseLayerFilterId,
  setIpdbGeoModelLoaded: s.setIpdbGeoModelLoaded,
  setIpdbControlWellsLoaded: s.setIpdbControlWellsLoaded,
  setGeoModelLoading: s.setGeoModelLoading,
  setIsDebuggingMap: s.setIsDebuggingMap,
  setMapState: s.setMapState,
  setViewLock: s.setViewLock,
  setMapExtent: s.setMapExtent,
  beginMapMove: s.beginMapMove,
  endMapMove: s.endMapMove,
  setFlyToGroup: s.setFlyToGroup,
  setFlyToSpecificPoint: s.setFlyToSpecificPoint,
  setFlyToExtent: s.setFlyToExtent,
  setAttentionPolygon: s.setAttentionPolygon,
  setExploitableArea: s.setExploitableArea,
  setXdaInterceptData: s.setXdaInterceptData,
  setXdaLineRequest: s.setXdaLineRequest,
  setNetworkDepthLegend: s.setNetworkDepthLegend,
  clearNetworkDepthLegend: s.clearNetworkDepthLegend,
  setShowNetworkOptions: s.setShowNetworkOptions,
  setHighlightContributingWellsForFacility: s.setHighlightContributingWellsForFacility,
  clearHighlightContributingWellsForFacility:
    s.clearHighlightContributingWellsForFacility,
  setWellNetworkMapRequest: s.setWellNetworkMapRequest,
  clearWellNetworkMapRequest: s.clearWellNetworkMapRequest,
  setWellNetworkFilterActivated: s.setWellNetworkFilterActivated,
  setNetworkEnvelope: s.setNetworkEnvelope,
  setSalesEnvelope: s.setSalesEnvelope,
  setHasToConfirmSize: s.setHasToConfirmSize,
  setFacilityHasToConfirmSize: s.setFacilityHasToConfirmSize,
  setFacilityOverrideSizeLimit: s.setFacilityOverrideSizeLimit,
  setWellsConfirmationChoice: s.setWellsConfirmationChoice,
  setFacilityConfirmationChoice: s.setFacilityConfirmationChoice,
  reset: s.reset
});

/** The rig/frac attribute the active well focus has pushed onto Color By. */
export const selectRigFracFocusColorBy = (
  s: MapStoreState
): Maybe<RigFracFocusAttribute> => s.rigFracFocusColorBy;

/** Layers reading their own saved Color By again because the user picked it. */
export const selectRigFracColorByDetached = (
  s: MapStoreState
): Readonly<Record<string, true>> => s.rigFracColorByDetached;
